import { apiUrls } from '../../domain/apiUrls';
import { getFileExtensionFromName } from '../utils';

const documentPreviewSupportedExtensions = ['pdf'];

export const isPdfFileName = (fileName: string): boolean =>
  getFileExtensionFromName(fileName)?.toLowerCase() === 'pdf';

export const isDataPreviewUrl = (value: string | null | undefined): boolean =>
  value?.startsWith('data:') ?? false;

export const isDocumentPreviewAvailable = ({
  extension,
  fileName
}: {
  extension?: string | null;
  fileName?: string | null;
}): boolean => {
  let resolvedExtension = extension;

  if (!resolvedExtension && fileName) {
    resolvedExtension = getFileExtensionFromName(fileName)?.toLowerCase();
  }

  if (!resolvedExtension) {
    return false;
  }

  return documentPreviewSupportedExtensions.includes(resolvedExtension);
};

export const buildAttachmentPreviewUrl = (downloadUrl: string): string => {
  const params = new URLSearchParams({ url: downloadUrl });

  return `${apiUrls.ATTACHMENTS_PREVIEW}?${params.toString()}`;
};

export const clampProgressValue = (value: number | undefined): number => {
  if (value === undefined || Number.isNaN(value)) {
    return 0;
  }

  return Math.min(100, Math.max(0, value));
};

export const createPdfPreviewBlobUrl = async (
  url: string,
  headers?: Record<string, string>,
  signal?: AbortSignal
): Promise<string> => {
  const response = await fetch(url, {
    credentials: 'include',
    headers,
    signal
  });

  if (!response.ok) {
    throw new Error(`Preview failed: ${response.status}`);
  }

  const blob = await response.blob();
  const pdfBlob =
    blob.type === 'application/pdf'
      ? blob
      : new Blob([blob], { type: 'application/pdf' });

  return URL.createObjectURL(pdfBlob);
};

export const isSameOriginUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

export type PdfPreviewSource =
  | { type: 'blob'; url: string }
  | { type: 'direct'; url: string };

export const resolvePdfPreviewSource = async (
  url: string,
  headers?: Record<string, string>,
  signal?: AbortSignal
): Promise<PdfPreviewSource> => {
  if (!isSameOriginUrl(url)) {
    return { type: 'direct', url };
  }

  const blobUrl = await createPdfPreviewBlobUrl(url, headers, signal);

  return { type: 'blob', url: blobUrl };
};
